/**
 * 094/096 — the Day popover's clock helpers (`CreateBlockPopover`): wall-clock strings within one
 * MYT day, and the 30-minute step its time fields use.
 *
 * 316: the proportional placement helpers that used to live here (`barGeometry`, `proportionOf`,
 * `instantAtProportion`, `rangeRect`) went with the old `Timeline.tsx`. Placement is now
 * `dnd-timeline`'s, and the time logic is in `dropLogic.ts`.
 */

import { MYT_OFFSET_MS } from './dropLogic.js';

/**
 * The wall-clock label for an instant, read as an offset INTO the window rather than as a
 * zone conversion — `from` is the window day's midnight in the window's own zone, so this
 * needs no `Intl`, no offset table and no clock. An instant before the window reads `00:00`
 * and one after it reads `24:00`, which is what a clamped bar's label should say.
 */
export function windowClock(instant: Date, from: Date, to: Date): string {
  const span = to.getTime() - from.getTime();
  const offset = Math.min(Math.max(instant.getTime() - from.getTime(), 0), span);
  const minutes = Math.round(offset / 60_000);
  const hh = String(Math.floor(minutes / 60)).padStart(2, '0');
  const mm = String(minutes % 60).padStart(2, '0');
  return `${hh}:${mm}`;
}

/** FR-003: the grid this feature edits on. Existing occupancy is never snapped to it. */
export const STEP_MINUTES = 30;

/**
 * 096, superseded 2026-09-21 (spec.md's own status-line note): the confirmation popover's time
 * fields now share this SAME step, instead of accepting an arbitrary minute. Rounds a "HH:mm"
 * clock to the nearest `STEP_MINUTES` boundary, wrapping past midnight rather than producing an
 * out-of-range hour.
 */
export function roundClockToHalfHour(clock: string): string {
  const m = /^(\d{1,2}):(\d{2})$/.exec(clock.trim());
  if (!m) return '00:00';
  const hh = Number(m[1]);
  const mm = Number(m[2]);
  const totalMinutes = hh * 60 + mm;
  const rounded = Math.round(totalMinutes / STEP_MINUTES) * STEP_MINUTES;
  const dayMinutes = 24 * 60;
  const wrapped = ((rounded % dayMinutes) + dayMinutes) % dayMinutes;
  const outHH = String(Math.floor(wrapped / 60)).padStart(2, '0');
  const outMM = String(wrapped % 60).padStart(2, '0');
  return `${outHH}:${outMM}`;
}

export type HalfHourMinute = 0 | 30;
export type Meridiem = 'AM' | 'PM';

/** Rounds first (see `roundClockToHalfHour`), then splits into the three select values. */
export function clockToParts(clock: string): {
  hour12: number;
  minute: HalfHourMinute;
  meridiem: Meridiem;
} {
  const rounded = roundClockToHalfHour(clock);
  const [hhStr, mmStr] = rounded.split(':') as [string, string];
  const hh24 = Number(hhStr);
  const minute = Number(mmStr) as HalfHourMinute;
  const meridiem: Meridiem = hh24 < 12 ? 'AM' : 'PM';
  const hour12Raw = hh24 % 12;
  const hour12 = hour12Raw === 0 ? 12 : hour12Raw;
  return { hour12, minute, meridiem };
}

/** The exact inverse of `clockToParts` for any of its own outputs. */
export function partsToClock(hour12: number, minute: HalfHourMinute, meridiem: Meridiem): string {
  const hour12Mod = hour12 % 12;
  const hh24 = meridiem === 'AM' ? hour12Mod : hour12Mod + 12;
  return `${String(hh24).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

/**
 * 096: the inverse of `windowClock` — an `HH:MM` clock reading back to an instant.
 *
 * The popover's time inputs speak the same clock the axis does (offset from the window's start,
 * which for a MYT day is MYT wall time), so a value typed there means the same thing the operator
 * reads off the header. Returns `null` for anything unparseable, so a half-typed value cannot be
 * mistaken for midnight.
 *
 * 🔴 Deliberately NOT snapped to `STEP_MINUTES`. Escaping the 30-minute grid is the entire point of
 * the popover (FR-003); the grid is a property of dragging, not a limit on what may be entered.
 */
export function instantAtClock(clock: string, from: Date): Date | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(clock.trim());
  if (!m) return null;
  const hh = Number(m[1]);
  const mm = Number(m[2]);
  if (hh > 23 || mm > 59) return null;
  return new Date(from.getTime() + (hh * 60 + mm) * 60_000);
}

/**
 * 382 (#1273): a MYT calendar date plus an `HH:MM` clock, read back to an instant. The popover's
 * clocks used to be offsets from the viewed day alone, so an end of "12:00 AM" meant the START of
 * that day and a block could never reach the next one. Malaysia is a fixed +8 h (no daylight
 * saving), so the arithmetic is on UTC and the browser's own zone cannot move the result.
 * `null` for anything that is not a real date or clock.
 */
export function instantAtDateClock(date: string, clock: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;
  const midnightUtc = Date.parse(`${date}T00:00:00.000Z`);
  if (Number.isNaN(midnightUtc) || new Date(midnightUtc).toISOString().slice(0, 10) !== date) {
    return null;
  }
  return instantAtClock(clock, new Date(midnightUtc - MYT_OFFSET_MS));
}

// `placePopover`/`PopoverPlacement` moved to `components/ui/popoverPlacement.ts` (115) — this
// function had no schedule-specific coupling, and `Popover.tsx` is now its second consumer.
